export type IdvFailureCode =
  | "DOC_IMAGE_QUALITY"
  | "DOC_COMPOSITION"
  | "DOC_AUTHENTICITY"
  | "DOC_UNSUPPORTED"
  | "DOC_GENERIC"
  | "SELFIE_LIVENESS"
  | "SELFIE_NO_MATCH"
  | "SELFIE_GENERIC"
  | "KYC_DOB_MISMATCH"
  | "KYC_NAME_MISMATCH"
  | "KYC_ADDRESS_MISMATCH"
  | "KYC_ID_MISMATCH"
  | "KYC_PHONE_MISMATCH"
  | "KYC_GENERIC"
  | "RISK_DECLINED"
  | "WATCHLIST_FLAGGED"
  | "INCOMPLETE"
  | "UNKNOWN";

export interface IdvFailureReason {
  code: IdvFailureCode;
  primaryStep: string;
  retriable: boolean;
}

/**
 * Camel-cased structural shape of the non-PII failure detail returned by
 * GET /app-idv/session/:sessionId/failure. Mirrors the IdvFailureDetails
 * interface persisted by resident-portal-api so the App and API share a
 * single end-to-end data shape.
 */
export interface IdvFailureDetailsLike {
  overallStatus?: string;
  steps?: {
    acceptTos?: string;
    verifySms?: string;
    kycCheck?: string;
    documentaryVerification?: string;
    selfieCheck?: string;
    watchlistScreening?: string;
    riskCheck?: string;
  };
  documentaryVerification?: {
    status?: string;
    documents?: Array<{
      status?: string;
      attempt?: number;
      imageQualityDetails?: {
        blurCheck?: string;
        dimensionsCheck?: string;
        glareCheck?: string;
      };
      fraudAnalysisDetails?: {
        detailCheck?: string;
        imageCompositionCheck?: string;
        integrityCheck?: string;
        issueDateCheck?: string;
        portraitDetailsCheck?: string;
        portraitPresenceCheck?: string;
        typeSupported?: string;
      };
      // Match codes comparing doc-extracted data to user-entered data.
      // `no_match` means the user typed something different from what was on the ID.
      // Plaid's dashboard surfaces these prominently when the doc itself is clean
      // but the applicant entered the wrong details.
      inputComparisons?: {
        name?: string;
        dateOfBirth?: string;
        expirationDate?: string;
        issuingCountry?: string;
      };
    }>;
  };
  selfieCheck?: {
    status?: string;
    selfies?: Array<{
      status?: string;
      attempt?: number;
      livenessCheck?: string;
      documentComparison?: string;
    }>;
  };
  kycCheck?: {
    status?: string;
    address?: string;
    name?: string;
    dateOfBirth?: string;
    idNumber?: string;
    phoneNumber?: string;
  };
  fetchedAt?: string;
}

/**
 * Shape returned by GET /app-idv/session/:sessionId/failure. `status` is the
 * uppercase session status from the API (FAILED / SUCCESS / ACTIVE / NOT_FOUND
 * / etc.) and `failureDetails` is the camelCase snapshot or null when the
 * session is not in a failed state.
 */
export interface IdvFailureEnvelope {
  status?: string;
  failureDetails?: IdvFailureDetailsLike | null;
}

function isFailed(v: string | undefined): boolean {
  return v === "failed";
}

function isNotMatch(v: string | undefined): boolean {
  return v !== undefined && v !== "match";
}

/**
 * Plaid sessions can hold multiple attempts in `documents[]` / `selfies[]`.
 * We pick the highest `attempt` value (falling back to array order when
 * attempt is missing) so the reason reflects the user's most recent try.
 */
function pickLatestAttempt<T extends { attempt?: number }>(
  items: T[]
): T | undefined {
  if (items.length === 0) return undefined;
  let best: T | undefined;
  let bestAttempt = -Infinity;
  for (let i = 0; i < items.length; i++) {
    const a = items[i].attempt ?? i;
    if (a >= bestAttempt) {
      bestAttempt = a;
      best = items[i];
    }
  }
  return best;
}

/**
 * Returns a specific reason code if the latest document attempt has an
 * identifiable failure, or null when documentary_verification is marked failed
 * but the latest attempt's checks all pass (a "derived" failure — usually
 * because risk_check or another step failed at the session level and pulled
 * documentary_verification down with it). When null is returned the caller
 * should fall through to the next failed step.
 *
 * Precedence within a single attempt:
 *   1. inputComparisons name/DOB mismatch — most actionable, user-correctable
 *   2. Image composition / quality / authenticity / supported-type checks
 */
function classifyDocumentary(
  vr: NonNullable<IdvFailureDetailsLike["documentaryVerification"]>
): IdvFailureCode | null {
  const docs = vr.documents || [];
  // Walk attempts newest-first, skipping ones with no usable signal. A user
  // can submit a junk final attempt (low quality, no extracted data, no fraud
  // checks) on top of a meaningful prior failure — picking the strict latest
  // would surface UNKNOWN even though we have a real reason on an earlier
  // attempt.
  const ordered = [...docs].sort(
    (a, b) => (b.attempt ?? -Infinity) - (a.attempt ?? -Infinity)
  );
  for (const doc of ordered) {
    const fraud = doc.fraudAnalysisDetails;
    const quality = doc.imageQualityDetails;
    const input = doc.inputComparisons;

    if (!fraud && !quality && !input) continue;

    // 1. User-entered data didn't match the ID (highest priority, most fixable).
    //    Reuse the KYC_* reason codes — the user-facing wording is identical
    //    whether the mismatch was surfaced via kyc_check or documentary_verification.
    if (isNotMatch(input?.dateOfBirth)) return "KYC_DOB_MISMATCH";
    if (isNotMatch(input?.name)) return "KYC_NAME_MISMATCH";

    // 2. Document image / authenticity checks.
    if (fraud?.imageCompositionCheck === "failed") return "DOC_COMPOSITION";
    if (
      quality?.blurCheck === "failed" ||
      quality?.glareCheck === "failed" ||
      quality?.dimensionsCheck === "failed"
    ) {
      return "DOC_IMAGE_QUALITY";
    }
    if (
      fraud?.detailCheck === "failed" ||
      fraud?.integrityCheck === "failed" ||
      fraud?.portraitDetailsCheck === "failed" ||
      fraud?.portraitPresenceCheck === "failed" ||
      fraud?.issueDateCheck === "failed"
    ) {
      return "DOC_AUTHENTICITY";
    }
    if (fraud?.typeSupported === "failed") return "DOC_UNSUPPORTED";

    // This attempt has data but no actionable failure → derived failure.
    // Caller should fall through to riskCheck / watchlistScreening etc.
    return null;
  }
  return null;
}

function classifySelfie(
  sc: NonNullable<IdvFailureDetailsLike["selfieCheck"]>
): IdvFailureCode {
  const selfies = sc.selfies || [];
  const last = pickLatestAttempt(selfies);
  if (!last) return "SELFIE_GENERIC";
  if (last.livenessCheck === "failed") return "SELFIE_LIVENESS";
  if (isNotMatch(last.documentComparison)) return "SELFIE_NO_MATCH";
  return "SELFIE_GENERIC";
}

function classifyKyc(
  kyc: NonNullable<IdvFailureDetailsLike["kycCheck"]>
): IdvFailureCode {
  if (isNotMatch(kyc.dateOfBirth)) return "KYC_DOB_MISMATCH";
  if (isNotMatch(kyc.name)) return "KYC_NAME_MISMATCH";
  if (isNotMatch(kyc.address)) return "KYC_ADDRESS_MISMATCH";
  if (isNotMatch(kyc.idNumber)) return "KYC_ID_MISMATCH";
  if (isNotMatch(kyc.phoneNumber)) return "KYC_PHONE_MISMATCH";
  return "KYC_GENERIC";
}

/**
 * Multi-signal variant of classifyDocumentary. Walks attempts newest-first,
 * skipping attempts with no usable signal, and returns ALL actionable
 * failures on the first meaningful attempt — not just the highest-precedence
 * one. Order preserved matches the single-reason precedence ladder.
 */
function collectDocumentary(
  vr: NonNullable<IdvFailureDetailsLike["documentaryVerification"]>
): IdvFailureCode[] {
  const docs = vr.documents || [];
  const ordered = [...docs].sort(
    (a, b) => (b.attempt ?? -Infinity) - (a.attempt ?? -Infinity)
  );
  for (const doc of ordered) {
    const fraud = doc.fraudAnalysisDetails;
    const quality = doc.imageQualityDetails;
    const input = doc.inputComparisons;
    if (!fraud && !quality && !input) continue;

    const out: IdvFailureCode[] = [];
    if (isNotMatch(input?.dateOfBirth)) out.push("KYC_DOB_MISMATCH");
    if (isNotMatch(input?.name)) out.push("KYC_NAME_MISMATCH");
    if (fraud?.imageCompositionCheck === "failed") out.push("DOC_COMPOSITION");
    if (
      quality?.blurCheck === "failed" ||
      quality?.glareCheck === "failed" ||
      quality?.dimensionsCheck === "failed"
    ) {
      out.push("DOC_IMAGE_QUALITY");
    }
    if (
      fraud?.detailCheck === "failed" ||
      fraud?.integrityCheck === "failed" ||
      fraud?.portraitDetailsCheck === "failed" ||
      fraud?.portraitPresenceCheck === "failed" ||
      fraud?.issueDateCheck === "failed"
    ) {
      out.push("DOC_AUTHENTICITY");
    }
    if (fraud?.typeSupported === "failed") out.push("DOC_UNSUPPORTED");
    return out;
  }
  return [];
}

function collectSelfie(
  sc: NonNullable<IdvFailureDetailsLike["selfieCheck"]>
): IdvFailureCode[] {
  const selfies = sc.selfies || [];
  const last = pickLatestAttempt(selfies);
  if (!last) return ["SELFIE_GENERIC"];
  const out: IdvFailureCode[] = [];
  if (last.livenessCheck === "failed") out.push("SELFIE_LIVENESS");
  if (isNotMatch(last.documentComparison)) out.push("SELFIE_NO_MATCH");
  if (out.length === 0) out.push("SELFIE_GENERIC");
  return out;
}

function collectKyc(
  kyc: NonNullable<IdvFailureDetailsLike["kycCheck"]>
): IdvFailureCode[] {
  const out: IdvFailureCode[] = [];
  if (isNotMatch(kyc.dateOfBirth)) out.push("KYC_DOB_MISMATCH");
  if (isNotMatch(kyc.name)) out.push("KYC_NAME_MISMATCH");
  if (isNotMatch(kyc.address)) out.push("KYC_ADDRESS_MISMATCH");
  if (isNotMatch(kyc.idNumber)) out.push("KYC_ID_MISMATCH");
  if (isNotMatch(kyc.phoneNumber)) out.push("KYC_PHONE_MISMATCH");
  if (out.length === 0) out.push("KYC_GENERIC");
  return out;
}

/**
 * Multi-signal variant of getIdvFailureReason. Returns every actionable
 * failure across documentary / selfie / kyc / risk / watchlist (deduped,
 * precedence-ordered). Useful when product wants to surface "top risk
 * indicators" as a list instead of one headline reason.
 */
export function getIdvFailureReasons(
  envelope: IdvFailureEnvelope | undefined | null
): IdvFailureReason[] {
  if (!envelope) return [];
  const status = envelope.status?.toUpperCase();
  if (status === "SUCCESS") return [];
  if (status === "ACTIVE" || status === "PENDING") {
    return [{ code: "INCOMPLETE", primaryStep: "verify_sms", retriable: true }];
  }
  if (status !== "FAILED") return [];

  const details = envelope.failureDetails;
  if (!details) {
    return [{ code: "UNKNOWN", primaryStep: "unknown", retriable: true }];
  }
  const steps = details.steps || {};
  const out: IdvFailureReason[] = [];

  if (
    isFailed(steps.documentaryVerification) &&
    details.documentaryVerification
  ) {
    for (const code of collectDocumentary(details.documentaryVerification)) {
      out.push({
        code,
        primaryStep: "documentary_verification",
        retriable: true,
      });
    }
  }
  if (isFailed(steps.selfieCheck) && details.selfieCheck) {
    for (const code of collectSelfie(details.selfieCheck)) {
      out.push({ code, primaryStep: "selfie_check", retriable: true });
    }
  }
  if (isFailed(steps.kycCheck) && details.kycCheck) {
    for (const code of collectKyc(details.kycCheck)) {
      out.push({ code, primaryStep: "kyc_check", retriable: true });
    }
  }
  if (isFailed(steps.riskCheck)) {
    out.push({
      code: "RISK_DECLINED",
      primaryStep: "risk_check",
      retriable: false,
    });
  }
  if (isFailed(steps.watchlistScreening)) {
    out.push({
      code: "WATCHLIST_FLAGGED",
      primaryStep: "watchlist_screening",
      retriable: false,
    });
  }

  const seen = new Set<IdvFailureCode>();
  const deduped = out.filter((r) =>
    seen.has(r.code) ? false : (seen.add(r.code), true)
  );

  if (deduped.length === 0) {
    return [{ code: "UNKNOWN", primaryStep: "unknown", retriable: true }];
  }
  return deduped;
}

/**
 * Map a session envelope from /app-idv/session/:sessionId/failure to a typed
 * reason code. Returns null for SUCCESS sessions or when the session is not
 * in a terminal failed state and there is nothing to surface.
 */
export function getIdvFailureReason(
  envelope: IdvFailureEnvelope | undefined | null
): IdvFailureReason | null {
  if (!envelope) return null;
  const status = envelope.status?.toUpperCase();
  if (status === "SUCCESS") return null;
  if (status === "ACTIVE" || status === "PENDING") {
    return { code: "INCOMPLETE", primaryStep: "verify_sms", retriable: true };
  }
  if (status !== "FAILED") return null;

  const details = envelope.failureDetails;
  if (!details) {
    return { code: "UNKNOWN", primaryStep: "unknown", retriable: true };
  }

  const steps = details.steps || {};

  if (
    isFailed(steps.documentaryVerification) &&
    details.documentaryVerification
  ) {
    const code = classifyDocumentary(details.documentaryVerification);
    if (code) {
      return {
        code,
        primaryStep: "documentary_verification",
        retriable: true,
      };
    }
    // Documentary is derived-failed (no specific doc issue on the latest
    // attempt). Fall through so the actual blocker — usually risk_check or
    // watchlist_screening — surfaces as the user-facing reason.
  }
  if (isFailed(steps.selfieCheck) && details.selfieCheck) {
    return {
      code: classifySelfie(details.selfieCheck),
      primaryStep: "selfie_check",
      retriable: true,
    };
  }
  if (isFailed(steps.kycCheck) && details.kycCheck) {
    return {
      code: classifyKyc(details.kycCheck),
      primaryStep: "kyc_check",
      retriable: true,
    };
  }
  if (isFailed(steps.riskCheck)) {
    return {
      code: "RISK_DECLINED",
      primaryStep: "risk_check",
      retriable: false,
    };
  }
  if (isFailed(steps.watchlistScreening)) {
    return {
      code: "WATCHLIST_FLAGGED",
      primaryStep: "watchlist_screening",
      retriable: false,
    };
  }

  return { code: "UNKNOWN", primaryStep: "unknown", retriable: true };
}
